import { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { motion, useReducedMotion } from 'motion/react';
import type { Variants } from 'motion/react';
import { DIAMOND_PATH } from '../ornaments/DiamondSpark';

/**
 * The signature motion effect: spark diamonds patrolling the straight legs
 * of a neon frame's border line. Renders an SVG overlay that must share the
 * frame art's viewBox so the guide path (the line-work's centerline, in
 * viewBox units) stays glued to the drawn line at every rendered size.
 *
 * Sparks NEVER round corners (2026-07-16 direction — corner sweeps read
 * choppy): each spark (or chain) owns one leg of the shape and moves back
 * and forth inside it, inset from the corner arcs. Every leg runs the same
 * motion profile in the same phase, so the effect stays symmetric — and
 * because the path direction alternates per leg (clockwise lap), identical
 * keyframes read as mirrored motion on opposite sides.
 *
 * Motion profiles are multi-waypoint keyframes with per-segment easing —
 * darts, settles, and dwells rather than metronome ping-pong — so the sparks
 * feel like they're scanning/processing, not oscillating. Implemented with
 * WAAPI (element.animate) so each spark gets numeric per-leg keyframes
 * (offset-distance is compositor-animated; no per-frame JS, no CSS var
 * lookups in keyframes).
 *
 * Reduced motion: animations are cancelled and each spark holds its `rest`
 * spot (the design's static ornament placement) via the base inline
 * offset-distance. Browsers without CSS motion path never show the sparks
 * (@supports gate in index.css). `active=false` (off-screen / hidden tab)
 * pauses in place.
 */

export type PatrolLeg = {
  /** Leg range along the guide path (fractions of total path length,
   *  corner-tangent to corner-tangent — sparks are inset from these). */
  start: number;
  end: number;
  /** Reduced-motion rest spot as an ABSOLUTE lap fraction (defaults to the
   *  center of the leg). Matches the design's static ornament placement. */
  rest?: number;
};

export type PatrolProfileName = 'scan' | 'drift' | 'pendulum';

/** One waypoint: at `at` (0..1 of the cycle) sit at `x` (0..1 of the patrol
 *  span); `easing` shapes the segment leaving this waypoint. First and last
 *  x match so the cycle loops seamlessly. */
type Waypoint = { at: number; x: number; easing?: string };

const dart = 'cubic-bezier(0.6, 0.05, 0.2, 1)'; // quick launch, soft landing
const glide = 'cubic-bezier(0.45, 0, 0.3, 1)'; // purposeful sweep
const drift = 'cubic-bezier(0.4, 0.1, 0.6, 0.9)'; // gentle correction

const PROFILES: Record<PatrolProfileName, Waypoint[]> = {
  /** Seek/settle "processing" pattern: dart out, settle back, probe the
   *  ends, dwell, re-check — reads as intelligent scanning. */
  scan: [
    { at: 0, x: 0.5, easing: dart },
    { at: 0.07, x: 0.84, easing: glide },
    { at: 0.13, x: 0.78, easing: drift },
    { at: 0.2, x: 0.82, easing: glide },
    { at: 0.29, x: 0.3, easing: drift },
    { at: 0.35, x: 0.36, easing: glide },
    { at: 0.43, x: 0.08, easing: drift },
    { at: 0.51, x: 0.13, easing: dart },
    { at: 0.58, x: 0.65, easing: drift },
    { at: 0.64, x: 0.6, easing: glide },
    { at: 0.72, x: 0.95, easing: drift },
    { at: 0.79, x: 0.9, easing: glide },
    { at: 0.9, x: 0.42, easing: drift },
    { at: 1, x: 0.5 },
  ],
  /** Calm wandering with near-holds — slower, breathing patrol. */
  drift: [
    { at: 0, x: 0.5, easing: glide },
    { at: 0.14, x: 0.86, easing: drift },
    { at: 0.2, x: 0.84, easing: glide },
    { at: 0.38, x: 0.14, easing: drift },
    { at: 0.46, x: 0.18, easing: glide },
    { at: 0.62, x: 0.72, easing: drift },
    { at: 0.7, x: 0.7, easing: glide },
    { at: 0.86, x: 0.32, easing: drift },
    { at: 1, x: 0.5 },
  ],
  /** Baseline end-to-end ping-pong (kept for comparison/tuning). */
  pendulum: [
    { at: 0, x: 0.02, easing: 'ease-in-out' },
    { at: 0.5, x: 0.98, easing: 'ease-in-out' },
    { at: 1, x: 0.02 },
  ],
};

// Dev-only profile A/B switch: append ?diamondProfile=scan|drift|pendulum to
// any page URL on the dev server to preview a profile live without a code
// change. Production always uses the `profile` prop.
const PROFILE_OVERRIDE: PatrolProfileName | null = (() => {
  if (!import.meta.env.DEV || typeof window === 'undefined') return null;
  const v = new URLSearchParams(window.location.search).get('diamondProfile');
  return v && v in PROFILES ? (v as PatrolProfileName) : null;
})();

type DiamondTravelProps = {
  viewBox: string;
  /** Centerline of the border line, in viewBox units. */
  guidePath: string;
  /** Legs to patrol — one spark (or chain) per leg. When omitted, the path
   *  is split into `count` equal legs (generic fallback). */
  legs?: PatrolLeg[];
  /** Equal-split leg count when `legs` is omitted. */
  count?: number;
  /** Seconds for one full patrol cycle. */
  durationS?: number;
  /** Diamond length along the travel direction, in viewBox units. */
  size?: number;
  /** Diamonds per leg (the design's chain-of-3 ornament). */
  clusterSize?: number;
  /** Distance between chain members along the path, in viewBox units. */
  clusterGapUnits?: number;
  /** Motion profile (see PROFILES). */
  profile?: PatrolProfileName;
  active: boolean;
  className?: string;
};

type Spark = {
  /** Patrol range start for this chain member (lap fraction). */
  base: number;
  /** Patrol travel span (lap fraction). */
  span: number;
  /** Reduced-motion rest position (lap fraction). */
  restAt: number;
};

/**
 * Entrance: the sparks are the FINALE of a section's reveal choreography —
 * they fade in after the frame has landed and the line-work has ignited
 * (variants inherit from the section's Reveal parent in HomePage; with
 * reduced motion there is no motion parent, so the sparks render static at
 * their rest spots, matching the design's ornaments).
 */
const sparksIn: Variants = {
  hidden: { opacity: 0 },
  visible: { opacity: 1, transition: { delay: 1.05, duration: 0.6, ease: 'easeOut' } },
};

export function DiamondTravel({
  viewBox,
  guidePath,
  legs,
  count = 3,
  durationS = 16,
  size = 88,
  clusterSize = 1,
  clusterGapUnits = 0,
  profile = 'scan',
  active,
  className,
}: DiamondTravelProps) {
  const reduced = useReducedMotion();
  const measureRef = useRef<SVGPathElement>(null);
  const sparkRefs = useRef<(SVGGElement | null)[]>([]);
  const animsRef = useRef<Animation[]>([]);
  const [totalLen, setTotalLen] = useState(0);

  useLayoutEffect(() => {
    setTotalLen(measureRef.current?.getTotalLength() ?? 0);
  }, [guidePath]);

  const legsKey = JSON.stringify(legs ?? count);
  const sparks: Spark[] = useMemo(() => {
    if (!totalLen) return [];
    const resolvedLegs: PatrolLeg[] =
      legs ?? Array.from({ length: count }, (_, i) => ({ start: i / count, end: (i + 1) / count }));
    // Inset from the leg ends: half the diamond plus a corner margin so the
    // glow never kisses the corner arcs.
    const inset = (size / 2 + size * 0.15) / totalLen;
    const gap = clusterGapUnits / totalLen;
    const chainExtent = (clusterSize - 1) * gap;
    return resolvedLegs.flatMap((leg) => {
      const lo = leg.start + inset;
      const span = Math.max(0, leg.end - inset - lo - chainExtent);
      // Rest = the chain leader's spot (members trail by the chain gap),
      // clamped into the patrol range.
      const rest = leg.rest ?? (leg.start + leg.end) / 2;
      const restLeader = Math.min(lo + span, Math.max(lo, rest));
      return Array.from({ length: clusterSize }, (_, k) => ({
        base: lo + k * gap,
        span,
        restAt: restLeader + k * gap,
      }));
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [totalLen, legsKey, count, size, clusterSize, clusterGapUnits]);

  // Build one WAAPI animation per spark: the shared profile mapped onto the
  // spark's own patrol range. Numeric keyframes — no CSS vars — keep the
  // animation compositor-friendly.
  useEffect(() => {
    animsRef.current.forEach((a) => a.cancel());
    animsRef.current = [];
    if (reduced || sparks.length === 0) return;
    if (typeof CSS === 'undefined' || !CSS.supports('offset-distance', '0%')) return;
    const waypoints = PROFILES[PROFILE_OVERRIDE ?? profile];
    sparks.forEach((spark, i) => {
      const el = sparkRefs.current[i];
      if (!el) return;
      const anim = el.animate(
        waypoints.map((w) => ({
          offset: w.at,
          offsetDistance: `${((spark.base + w.x * spark.span) * 100).toFixed(4)}%`,
          ...(w.easing ? { easing: w.easing } : {}),
        })),
        { duration: durationS * 1000, iterations: Infinity },
      );
      anim.pause(); // the play-state effect below syncs to `active`
      animsRef.current.push(anim);
    });
    const anims = animsRef.current;
    return () => {
      anims.forEach((a) => a.cancel());
      animsRef.current = [];
    };
  }, [sparks, profile, durationS, reduced]);

  useEffect(() => {
    animsRef.current.forEach((a) => (active ? a.play() : a.pause()));
  }, [active, sparks, profile, durationS, reduced]);

  return (
    <motion.svg
      viewBox={viewBox}
      preserveAspectRatio="none"
      aria-hidden="true"
      variants={sparksIn}
      className={`pointer-events-none absolute inset-0 h-full w-full overflow-visible ${className ?? ''}`}
    >
      <path ref={measureRef} d={guidePath} fill="none" />
      {sparks.map((spark, i) => (
        <g
          key={i}
          ref={(el) => {
            sparkRefs.current[i] = el;
          }}
          className="diamond-travel"
          style={{
            offsetPath: `path("${guidePath}")`,
            // Base value = reduced-motion rest spot (the animation overrides
            // it while running).
            offsetDistance: `${(spark.restAt * 100).toFixed(4)}%`,
          }}
        >
          {/* rotate(90): the spark's long axis (y) onto the path axis (x). */}
          <path
            d={DIAMOND_PATH}
            transform={`scale(${size / 2}) rotate(90)`}
            className="diamond-travel-spark"
          />
        </g>
      ))}
    </motion.svg>
  );
}
